// ApiError: structured error type for failed HTTP responses. Carries status,
// code, body, endpoint, method, and a retry hint. reportError writes a
// single structured object per error to console.error. No remote reporting.

export class ApiError extends Error {
    constructor({ status, message, code, body, endpoint, method }) {
        super(message);
        this.name = "ApiError";
        this.status = status;     // HTTP status (number). 0 for network failures.
        this.code = code;         // body.error (could be code like "AUTH_ERROR" or sentence like "User not found")
        this.body = body;         // Full parsed response body if JSON, else null
        this.endpoint = endpoint; // Path that was called
        this.method = method;     // HTTP method
        this.isRetryable =
            status === 0 ||
            status === 408 ||
            status === 429 ||
            (typeof status === "number" && status >= 500);
    }
}

export function isApiError(error) {
    return error instanceof ApiError;
}

export function reportError(error, context = {}) {
    const entry = {
        timestamp: new Date().toISOString(),
        name: error.name,
        message: error.message,
        stack: error.stack,
        ...context,
        ...(error instanceof ApiError && {
            status: error.status,
            code: error.code,
            endpoint: error.endpoint,
            method: error.method,
        }),
    };
    console.error("[error]", entry);
    return entry;
}

// User-facing error copy keyed by HTTP status.

export function getApiErrorMessage(status, context = "default") {
    if (status === 401) {
        if (context === "login") return "Wrong email or password.";
        if (context === "totp")  return "Incorrect code. Please try again.";
        return "Your session has expired. Please log in again.";
    }
    if (status === 403) return "You do not have permission to do that.";
    if (status === 404) return context === "totp"
        ? "Your login session has timed out. Please start again."
        : "That could not be found. It may have been deleted.";
    if (status === 409) return "That already exists.";
    if (status === 429) return "Too many attempts. Please wait a minute before trying again.";
    if (status >= 500 && status < 600) {
        return "Gobby is temporarily unavailable. Please try again shortly.";
    }
    if (status === "network") return "Network error. Check your internet connection and try again.";
    if (context === "respond") return "Your answer could not be submitted. Please try again.";
    return "Something went wrong. Please try again.";
}

// Reduces a thrown error to a status the message helper can use.
export function classifyError(error) {
    if (error?.status) return error.status;
    if (error?.message === "Network Error" || error?.name === "AbortError") return "network";
    const match = error?.message?.match(/^HTTP (\d{3})/);
    return match ? parseInt(match[1], 10) : "unknown";
}
